import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { CopyIcon } from '@phosphor-icons/react'
import { useTable } from 'react-table'
import DropdownButton from '@components/common/DropdownButton'
import { getMetricValues } from "@utils"
import {
  metricOptions,
  countrySizeOptions,
  getCountrySize,
} from "@components/helpers/SizeAndMetrics"

// assets
import bust from "@images/Products/measure-breast.webp"
import waist from "@images/Products/measure-waist.webp"
import hips from "@images/Products/measure-hips.webp"

const SwimSection = ({
  data, 
  howToMeasureContainerClassName = '', 
  howToMeasureClassName = 'how-to-measure-item', 
  dropDownContainerStyle = 'flex justify-between items-center'
}) => {
  const [countrySize, setCountrySize] = useState([])
  const [metricUsed, setMetricUsed] = useState('IN')
  
  useEffect(() => {
    setCountrySize(getCountrySize('US'))
  }, [])

  const sizeColumns = useMemo(() => [
    {
      Header: '',
      accessor: 'label',
      Cell: ( {value} ) => <span className='font-semibold'>{value}</span>,
    },
    {
      Header: 'XS',
      accessor: 'xs',
      Cell: ( {value} ) => <span>{getMetricValues(value, metricUsed)}</span>,
    },
    {
      Header: 'S',
      accessor: 's',
      Cell: ( {value} ) => <span>{getMetricValues(value, metricUsed)}</span>,
    },
    {
      Header: 'M',
      accessor: 'm',
      Cell: ( {value} ) => <span>{getMetricValues(value, metricUsed)}</span>,
    },
    {
      Header: 'L',
      accessor: 'l',
      Cell: ( {value} ) => <span>{getMetricValues(value, metricUsed)}</span>,
    },
    {
      Header: 'XL',
      accessor: 'xl',
      Cell: ( {value} ) => <span>{getMetricValues(value, metricUsed)}</span>,
    },
    {
      Header: 'XXL',
      accessor: 'xxl',
      Cell: ( {value} ) => <span>{getMetricValues(value, metricUsed)}</span>,
    },
  ], [metricUsed])

  const sizeData = useMemo(() => [
    { ...countrySize },
    {
      label: 'BAND WIDTH',
      xs: '02',
      s: '2-4',
      m: '6-8',
      l: '8-10',
      xl: '10-12',
      xxl: '12-14',
    },
    {
      label: 'CUP SIZE',
      xs: 'AA-A',
      s: 'A-B',
      m: 'B-C',
      l: 'C-D',
      xl: 'D-DDD',
      xxl: 'G',
    },
    {
      label: 'WAIST',
      xs: '23-24"',
      s: '24-25"',
      m: '26-28"',
      l: '29-30"',
      xl: '31-32"',
      xxl: '33-34"',
    },
    {
      label: 'HIPS',
      xs: '30-31"',
      s: '31-33"',
      m: '34-36"',
      l: '37-38"',
      xl: '39-40"',
      xxl: '41-44"',
    },
  ], [countrySize])
  
  const handleCountrySizeChange = (size) => {
    setCountrySize(getCountrySize(size.label))
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns: sizeColumns, data: sizeData })

  return (
    <React.Fragment>
      <div className={`${dropDownContainerStyle}`}>
        <div className="">
          <DropdownButton
            defaultOption={countrySizeOptions[0]}
            options={countrySizeOptions}
            onClick={(size) => handleCountrySizeChange(size)}
          />
        </div>
        <div className="flex flex-nowrap gap-x-3 overflow-visible">
          <DropdownButton
            label={
              <div className="space-x-2 text-secondary">
                <span>METRICS</span>
                <span>:</span>
              </div>
            }
            defaultOption={metricOptions[0]}
            options={metricOptions}
            onClick={(metric) => setMetricUsed(metric.id)}
          />
        </div>
      </div>

      <div className="">
        <div className="w-full p-2 bg-[#454545] text-white text-center">
          BIKINI MEASUREMENTS
        </div>
        <div className="overflow-x-auto">
          <table {...getTableProps()} className="min-w-full">
            <thead>
              {headerGroups?.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup?.headers?.map((column) => (
                    <th
                      {...column.getHeaderProps()}
                      className="px-6 py-3 text-center border border-slate-200"
                    >
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        className="px-2 py-3 text-sm border border-slate-200 text-gray-700 text-center"
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`py-16 space-y-4 ${howToMeasureContainerClassName}`}>
          <div className={`${howToMeasureClassName}`}>
            <img src={bust} alt="bust size" />
            <div className="flex-1 space-y-2">
              <h6>1. BUST</h6>
              <p className="text-md">
                Measure from the fullest part of your bust, this should be above
                the bra band.
              </p>
            </div>
          </div>

          <div className={`${howToMeasureClassName}`}>
            <img src={waist} alt="waist size" />
            <div className="flex-1 space-y-2">
              <h6>2. WAIST</h6>
              <p className="text-md">
                Place tape just above your navel and wrap it around your body.
              </p>
            </div>
          </div>

          <div className={`${howToMeasureClassName}`}>
            <img src={hips} alt="hip size" />
            <div className="flex-1 space-y-2">
              <h6>3. HIPS</h6>
              <p className="text-md">
                Take this measurement from the widest point of your hips.
              </p>
            </div>
          </div>
        </div>

        <p className="inline-block w-full text-md">
          For sizing questions, Email us at{" "}
          <a href="mailto:aloha@blackboughswim.com" target="_blank" className="link-style mx-1">
            <span className="flex gap-x-2 items-center">
              aloha@blackboughswim.com <CopyIcon size={20} />
            </span>
          </a>
          or contact us via our{" "}
          <Link to="/contact-us" className="link-style mx-1">
            <span className="flex gap-x-2 items-center">Contact Form.</span>
          </Link>{" "}
          We’re always eager to help!
        </p>
      </div>
    </React.Fragment>
  );
}

export default SwimSection